import React, { useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { CalendarOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import salesOrderService, { type SalesOrder } from '@/services/salesOrder.service';
import salesOrderTimesheetService from '@/services/salesOrderTimesheet.service';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const { Title, Text } = Typography;

const statusColor: Record<string, string> = {
  Draft: 'default',
  Submitted: 'processing',
  Approved: 'success',
  Rejected: 'error',
  Invoiced: 'purple',
};

function periodLabel(year: number, month: number) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Register of sales-order timesheet documents (rental / timesheet fulfillment).
 */
const SalesOrderTimesheetRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [createOpen, setCreateOpen] = useState(false);
  const [customerId, setCustomerId] = useState<string | undefined>();
  const [salesOrderId, setSalesOrderId] = useState<string | undefined>();
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
  const [periodMonth, setPeriodMonth] = useState(new Date().getMonth() + 1);
  const [form] = Form.useForm();

  const { data: customers = [] } = useActiveCustomers();

  const { data: timesheetOrders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ['sales-orders-timesheet-open', customerId],
    queryFn: () =>
      salesOrderService.getAll({
        fulfillment_mode: 'timesheet',
        customer_id: customerId,
      }),
    enabled: createOpen && !!customerId,
  });

  const openOrders = useMemo(
    () =>
      (Array.isArray(timesheetOrders) ? timesheetOrders : []).filter(
        (o: SalesOrder) => o.order_status !== 'Completed' && o.order_status !== 'Cancelled'
      ),
    [timesheetOrders]
  );

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['sales-order-timesheets-register', debouncedSearch, statusFilter],
    queryFn: () =>
      salesOrderTimesheetService.listAll({
        search: debouncedSearch || undefined,
        status: statusFilter,
      }),
  });

  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const createMutation = useMutation({
    mutationFn: () =>
      salesOrderTimesheetService.create(salesOrderId!, {
        period_year: periodYear,
        period_month: periodMonth,
        copy_people_from_prior_month: true,
      }),
    onSuccess: (row) => {
      message.success('Timesheet created');
      setCreateOpen(false);
      setCustomerId(undefined);
      setSalesOrderId(undefined);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['sales-order-timesheets-register'] });
      navigate(`/growth/orders/${row.sales_order_id}/timesheets?timesheetId=${row.id}`);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Create failed'),
  });

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              <CalendarOutlined style={{ marginRight: 8 }} />
              Timesheet for Rental
            </Title>
            <Text type="secondary">
              Monthly attendance timesheets. Create a timesheet, approve, then invoice.
            </Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Add new timesheet
          </Button>
        </Space>

        <Card size="small">
          <Space wrap style={{ marginBottom: 16 }}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search SO, customer, or period"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 280 }}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 140 }}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              options={['Draft', 'Submitted', 'Approved', 'Rejected', 'Invoiced'].map((s) => ({
                value: s,
                label: s,
              }))}
            />
            <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>

          <Table
            rowKey="id"
            loading={isLoading}
            dataSource={rows}
            pagination={{ pageSize: 20 }}
            columns={[
              {
                title: 'Period',
                width: 110,
                render: (_: unknown, row) => periodLabel(row.period_year, row.period_month),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 120,
                render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s}</Tag>,
              },
              {
                title: 'Customer order',
                dataIndex: 'sales_order_number',
                render: (val: string, row) => (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => navigate(`/growth/orders/${row.sales_order_id}`)}
                  >
                    {val}
                  </Button>
                ),
              },
              { title: 'Customer', dataIndex: 'customer_name' },
              {
                title: '',
                key: 'actions',
                width: 120,
                render: (_: unknown, row) => (
                  <Button
                    type="primary"
                    size="small"
                    icon={<CalendarOutlined />}
                    onClick={() =>
                      navigate(
                        `/growth/orders/${row.sales_order_id}/timesheets?timesheetId=${row.id}`
                      )
                    }
                  >
                    Open
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      </Space>

      <Modal
        title="Add new timesheet"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          setCustomerId(undefined);
          setSalesOrderId(undefined);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isPending}
        okText="Create timesheet"
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={() => {
            if (!salesOrderId) {
              message.warning('Select a customer order');
              return;
            }
            createMutation.mutate();
          }}
        >
          <Form.Item label="Customer" required>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select customer"
              value={customerId}
              onChange={(v) => {
                setCustomerId(v);
                setSalesOrderId(undefined);
              }}
              options={(customers || []).map((c: any) => ({
                value: c.id,
                label: c.customer_name || c.name,
              }))}
            />
          </Form.Item>
          <Form.Item label="Customer order" required>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={customerId ? 'Select timesheet customer order' : 'Select customer first'}
              disabled={!customerId}
              loading={ordersLoading}
              value={salesOrderId}
              onChange={setSalesOrderId}
              options={openOrders.map((o) => ({
                value: o.id,
                label: `${o.sales_order_number} (${o.order_status})`,
              }))}
              notFoundContent={
                customerId && !ordersLoading
                  ? 'No open timesheet-fulfillment orders for this customer'
                  : undefined
              }
            />
          </Form.Item>
          <Space>
            <Form.Item label="Year" required>
              <InputNumber
                min={2000}
                max={2100}
                value={periodYear}
                onChange={(v) => setPeriodYear(Number(v) || periodYear)}
              />
            </Form.Item>
            <Form.Item label="Month" required>
              <InputNumber
                min={1}
                max={12}
                value={periodMonth}
                onChange={(v) => setPeriodMonth(Number(v) || periodMonth)}
              />
            </Form.Item>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            One timesheet per calendar month per customer order. Import Excel or enter attendance after
            create.
          </Text>
        </Form>
      </Modal>
    </div>
  );
};

export default SalesOrderTimesheetRegisterPage;
